import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import useAssetFilterType from "domains/assets/hooks/useAssetFilterType";
import useAssetSort from "domains/assets/hooks/useAssetSort";
import useAssetView from "domains/assets/hooks/useAssetView";
import Deffered from "domains/commons/components/Deffered";
import { useDebounce } from "domains/commons/hooks/useDebounce";
import useDeepMemo from "domains/commons/hooks/useDeepMemo";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import useExploreTabsStatus from "domains/inference/hooks/useExploreTabsStatus";
import {
  ExploreSuggestArgs,
  ExploreTab,
} from "domains/inference/interfaces/Explore";
import { SearchProvider } from "domains/search/contexts/SearchProvider";
import { useAssetsSearch } from "domains/search/hooks/useSearch";
import {
  Tab,
  TabIndicator,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "domains/ui/components/Tabs";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

import { Box } from "@chakra-ui/react";

import ExploreContent, { ExploreContentProps } from "./ExploreContent";
import ExploreTabs from "./ExploreTabs";

export type InferenceGeneratorTabsHandle = {
  reset: () => void;
};

type InferenceGeneratorContentProps<C extends object = object> =
  PropsWithChildren &
    Pick<
      ExploreProps<C>,
      "type" | "scrollRef" | "cardActionsProps" | "CardActionsComponent"
    > & {
      tabsRef: React.Ref<{
        reset: () => void;
      }>;
      suggestArgs:
        | {
            prompt?: string;
            style?: string;
            model?: GetModelsByModelIdApiResponse["model"];
          }
        | undefined;
    };

export default function InferenceGeneratorContent<C extends object = object>({
  tabsRef,
  children,
  suggestArgs,
  ...exploreProps
}: InferenceGeneratorContentProps<C>) {
  const [tabIndex, setTabIndex] = useState(0);
  const { tabs, tabsStatus, markAsUpToDate, isSuggestsAvailable } =
    useExploreTabsStatus(exploreProps.type, suggestArgs);
  const [exploreTab, setExploreTab] = useState<string | undefined>(
    isSuggestsAvailable ? "suggests" : undefined
  );
  const [lastScrollTop, setLastScrollTop] = useState<number>(0);
  const [isExploreRendered, setIsExploreRendered] = useState<boolean>(false);

  const suggestPrompt = useDebounce(
    suggestArgs?.prompt,
    tabIndex !== 1 ? 5_000 : undefined
  );
  const suggestStyle = suggestArgs?.style;
  const suggestModel = suggestArgs?.model;
  const suggestModelId = suggestArgs?.model?.id;

  const exploreSuggestArgs = useMemo(
    () =>
      suggestModelId || suggestStyle
        ? {
            modelId: suggestModelId,
            style: suggestStyle,
            prompt: suggestPrompt,
          }
        : undefined,
    [suggestPrompt, suggestModelId, suggestStyle]
  );
  const [lastSuggestArgs, setLastSuggestArgs] = usePersistedState<
    ExploreSuggestArgs | undefined
  >(PersistedStateKey.LAST_EXPLORE_SUGGEST_ARGS, { defaultValue: undefined });

  const isSuggestsNotified = useMemo(
    () => !_.isEqual(exploreSuggestArgs, lastSuggestArgs),
    [exploreSuggestArgs, lastSuggestArgs]
  );

  const fmViewProps = useAssetView({
    options:
      exploreProps.type === "image" ? ["masonry", "fill", "fit"] : ["masonry"],
  });
  const { allAssetsTypeArgs: imageTypesQueryArgs } = useAssetFilterType({
    assetType: exploreProps.type,
    excludeUpload: true,
    showVectorization: true,
    avoidPersistedState: true,
  });
  const { searchSortArgs, fmHeaderSortProps } = useAssetSort(
    _.compact(["relevance", "createdAtTSDesc", "createdAtTSAsc"])
  );
  const tagsArgs = useDeepMemo(
    exploreTab !== "suggests" && exploreTab ? { tags: [exploreTab] } : {}
  );

  const {
    files: images,
    resultCount: imagesCount,
    isLoading: isLoadingImages,
    loadMore: loadMoreImages,
    hasMore: hasMoreImages,
  } = useAssetsSearch({
    isEmptyQueryAllowed: !!(
      exploreTab &&
      (exploreTab !== "suggests" || suggestArgs)
    ),
    ...imageTypesQueryArgs,
    ...searchSortArgs,
    ...tagsArgs,
    query: suggestPrompt ?? "",
    modelIds:
      exploreTab === "suggests" && suggestModelId
        ? [suggestModelId]
        : undefined,
    styles:
      exploreTab === "suggests" && suggestStyle ? [suggestStyle] : undefined,
    privacy: exploreTab === "suggests" ? ["private", "public"] : "public",
    aiBoost: true,
  });

  const isEmpty = !isSuggestsAvailable && tabs?.length === 0;

  const exploreFileManagerProps = useMemo(
    () => ({
      files: images,
      isLoading: isLoadingImages,
      hasMore: hasMoreImages,
      onEndReached: loadMoreImages,
      ...fmViewProps,
      headerSortProps: fmHeaderSortProps,
      scrollRef: exploreProps.scrollRef,
      CardActionsComponent: exploreProps.CardActionsComponent,
      cardActionsProps: exploreProps.cardActionsProps,
    }),
    [
      exploreProps.CardActionsComponent,
      exploreProps.cardActionsProps,
      exploreProps.scrollRef,
      fmHeaderSortProps,
      fmViewProps,
      hasMoreImages,
      images,
      isLoadingImages,
      loadMoreImages,
    ]
  );

  // ----------------------------------

  const resetTabs = useCallback(() => setTabIndex(0), [setTabIndex]);

  const handleSuggestsEnter = useCallback(() => {
    setLastSuggestArgs(exploreSuggestArgs);
  }, [setLastSuggestArgs, exploreSuggestArgs]);

  const handleVary = useCallback(() => {
    resetTabs();
  }, [resetTabs]);

  const handleExploreFirstRender = useCallback(() => {
    setIsExploreRendered(true);
  }, [setIsExploreRendered]);

  // ----------------------------------

  useEffect(() => {
    if (!isExploreRendered) return;
    if (tabIndex !== 1) return;
    let timeout: NodeJS.Timeout;

    const handleScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      const lastScrollTop = target.scrollTop;
      if (timeout) clearTimeout(timeout);
      timeout = setTimeout(() => {
        setLastScrollTop(lastScrollTop);
      }, 500);
    };

    const scrollableElement = exploreProps.scrollRef?.current;
    scrollableElement?.addEventListener("scroll", handleScroll);
    return () => {
      scrollableElement?.removeEventListener("scroll", handleScroll);
    };
  }, [tabIndex, isExploreRendered, exploreProps.scrollRef]);

  useEffect(() => {
    if (exploreTab === undefined && isSuggestsAvailable) {
      setExploreTab("suggests");
    }
  }, [exploreTab, isSuggestsAvailable]);

  useImperativeHandle(tabsRef, () => {
    return {
      reset: resetTabs,
    };
  });

  // ----------------------------------

  return (
    <Box>
      <Tabs
        colorScheme="white"
        index={tabIndex}
        isLazy
        onChange={(index) => setTabIndex(index)}
        size="lg"
        variant="light"
      >
        <Box
          pos="sticky"
          zIndex="sticky"
          top={0}
          pt={1}
          bgColor="background.500"
        >
          <TabList px={0}>
            <Tab>Session</Tab>
            <Tab pos="relative">
              <span>Explore</span>

              {isSuggestsNotified && tabIndex !== 1 && (
                <Box pos="absolute" right={-4}>
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="50%"
                    bgColor="primary.500"
                  />
                </Box>
              )}
            </Tab>
          </TabList>
          <TabIndicator key={`tab-indicator-${tabIndex}`} />
        </Box>

        <TabPanels>
          <TabPanel>
            <Deffered timeout={200}>
              <Box>{children}</Box>
            </Deffered>
          </TabPanel>
          <TabPanel>
            <Deffered timeout={200}>
              <SearchProvider>
                <Explore
                  {...exploreProps}
                  {...exploreFileManagerProps}
                  isLoadingImages={isLoadingImages}
                  lastScrollTop={lastScrollTop}
                  tabs={tabs}
                  tabsStatus={tabsStatus}
                  isEmpty={isEmpty}
                  activeTab={exploreTab}
                  suggestModel={suggestModel}
                  isSuggestsAvailable={isSuggestsAvailable}
                  isSuggestsNotified={isSuggestsNotified}
                  imagesCount={imagesCount ?? 0}
                  onSuggestsEnter={handleSuggestsEnter}
                  onVary={handleVary}
                  onTabMarkedAsSeen={markAsUpToDate}
                  onTabChange={setExploreTab}
                  onFirstRender={handleExploreFirstRender}
                />
              </SearchProvider>
            </Deffered>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}

// ------------------------------------

type ExploreProps<C extends object = object> = PropsWithChildren &
  Omit<ExploreContentProps<C>, "value"> & {
    isEmpty?: boolean;
    tabs: ExploreTab[] | undefined;
    activeTab?: string;
    tabsStatus: { [key: string]: boolean };
    isSuggestsNotified?: boolean;
    isSuggestsAvailable?: boolean;
    suggestModel?: GetModelsByModelIdApiResponse["model"];
    onTabMarkedAsSeen: (tab: string) => void;
    onSuggestsEnter: () => void;
    onTabChange: React.Dispatch<React.SetStateAction<string | undefined>>;
  };

function Explore<C extends object = object>({
  type,
  tabs,
  activeTab,
  tabsStatus,
  isEmpty = false,
  isSuggestsNotified = false,
  isSuggestsAvailable = false,
  suggestModel,
  scrollRef,
  onTabMarkedAsSeen,
  onSuggestsEnter,
  onTabChange,
  ...exploreContentProps
}: ExploreProps<C>) {
  const changeExploreTab = useCallback<
    React.Dispatch<React.SetStateAction<string | undefined>>
  >(
    (value) => {
      scrollRef?.current?.scrollTo(0, 0);
      onTabChange(value);
    },
    [scrollRef, onTabChange]
  );

  // ----------------------------------

  useEffect(() => {
    if (!activeTab) return;
    if (activeTab === "suggests") {
      onSuggestsEnter();
    } else {
      onTabMarkedAsSeen(activeTab);
    }
  }, [onTabMarkedAsSeen, onSuggestsEnter, activeTab]);

  useEffect(() => {
    if (isSuggestsAvailable || tabs === undefined || tabs.length === 0) return;
    changeExploreTab((activeTab) =>
      activeTab === "suggests" ? tabs[0].id : activeTab
    );
  }, [changeExploreTab, isSuggestsAvailable, tabs]);

  useEffect(() => {
    if (tabs === undefined || tabs.length === 0) return;
    changeExploreTab((activeTab) => activeTab ?? tabs[0].id);
  }, [changeExploreTab, tabs]);

  // ----------------------------------

  return (
    <>
      {!isEmpty && (
        <ExploreTabs
          tabs={tabs}
          suggestModel={suggestModel}
          isSuggestsAvailable={isSuggestsAvailable}
          isSuggestsNotified={isSuggestsNotified && activeTab !== "suggests"}
          value={activeTab ?? ""}
          status={tabsStatus}
          onChange={changeExploreTab}
        />
      )}

      <ExploreContent
        type={type}
        value={activeTab}
        scrollRef={scrollRef}
        {...exploreContentProps}
        isEmpty={isEmpty}
      />
    </>
  );
}
